<script setup lang="ts">
import {
  tpgIconClose,
  tpgIconFileDownload,
  tpgIconInfo,
  tpgIconOpenInNew,
} from '@tpg/grammar-web-icons';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  GrammarBadge,
  GrammarCard,
  GrammarQuaternaryButton,
  GrammarSectionHeader,
} from '@nora-monorepo/grammar-ui';

import type { GalleryFile } from '#ui-shared/components/gallery/gallery.types';
import {
  getGalleryFileSource,
  getInlineGalleryFileUrl,
} from '#ui-shared/components/gallery/gallery.types';
import { truncateText } from '#ui-shared/utils/format-utils';
import {
  isImage,
  prettyPrintFileSize,
} from '#ui-shared/utils/pretty-print-file-size';

interface GallerySideBarProps {
  file: GalleryFile;
}

const props = defineProps<GallerySideBarProps>();

defineEmits<{
  close: [];
}>();

const { d } = useI18n();
const isInfoBoxOpen = ref(false);
const showFullName = ref(false);

const source = computed(() => getGalleryFileSource(props.file));
const inlineUrl = computed(() => getInlineGalleryFileUrl(props.file));

const displayName = computed(() => {
  return showFullName.value
    ? props.file.fileName
    : truncateText(props.file.fileName, 30);
});

const displayUploadedAt = computed(() => {
  return props.file.createdAt
    ? d(props.file.createdAt, 'long')
    : null;
});

const imageDimensions = computed(() => {
  const { height, width } = props.file;

  return typeof height === 'number' && typeof width === 'number'
    ? `${height}x${width} (px)`
    : null;
});

function openInNewWindow(): void {
  if (inlineUrl.value) {
    window.open(inlineUrl.value, '_blank', 'noopener,noreferrer');
  }
}
</script>

<template>
  <div
    class="flex flex-col absolute bg-[#162936]/100 rounded-2xl top-10 right-10 z-9998 border border-[#263C4B]"
    @click.stop
  >
    <div class="p-1">
      <GrammarQuaternaryButton
        :icon-template="tpgIconOpenInNew"
        @click="openInNewWindow"
      />
      <a
        v-if="source"
        :href="source"
        :download="file.fileName"
      >
        <GrammarQuaternaryButton :icon-template="tpgIconFileDownload" />
      </a>
      <GrammarQuaternaryButton
        :icon-template="tpgIconInfo"
        @click="isInfoBoxOpen = !isInfoBoxOpen"
      />
      <GrammarQuaternaryButton
        :icon-template="tpgIconClose"
        @click="$emit('close')"
      />
    </div>

    <div
      v-if="isInfoBoxOpen"
      class="absolute top-full w-auto z-9999 right-0 pt-1"
    >
      <GrammarCard primary-label="File info">
        <template #card-row-1>
          <div class="flex flex-col w-full pb-2 px-3">
            <div class="cursor-pointer" @click="showFullName = !showFullName">
              <GrammarSectionHeader
                class="text-[#8EA7B8]"
                header-type="list-header"
                label="File name"
              />
              <GrammarBadge
                emphasis="no-background"
                size="lg"
                class="px-2"
                :label="displayName"
              />
            </div>

            <div v-if="displayUploadedAt">
              <GrammarSectionHeader
                class="text-[#8EA7B8]"
                header-type="list-header"
                label="Uploaded at"
              />
              <GrammarBadge
                class="text-[#8EA7B8] px-2"
                emphasis="no-background"
                :label="displayUploadedAt"
                size="lg"
              />
            </div>

            <GrammarSectionHeader
              class="text-[#8EA7B8]"
              header-type="list-header"
              label="File size"
            />
            <GrammarBadge
              class="text-[#8EA7B8] px-2"
              emphasis="no-background"
              :label="prettyPrintFileSize(file.size)"
              size="lg"
            />

            <GrammarSectionHeader
              class="text-[#8EA7B8]"
              header-type="list-header"
              label="File format"
            />
            <GrammarBadge
              class="text-[#8EA7B8] px-2"
              emphasis="no-background"
              :label="file.mimeType"
              size="lg"
            />

            <template v-if="isImage(file.mimeType) && imageDimensions">
              <GrammarSectionHeader
                class="text-[#8EA7B8]"
                header-type="list-header"
                label="Dimensions"
              />
              <GrammarBadge
                class="text-[#8EA7B8] px-2"
                emphasis="no-background"
                :label="imageDimensions"
                size="lg"
              />
            </template>
          </div>
        </template>
      </GrammarCard>
    </div>
  </div>
</template>
